import { Link, Outlet, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  BookOpen,
  Code2,
  CreditCard,
  Globe,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Inbox,
  Menu,
  Plug,
  Scale,
  ScrollText,
  Settings,
  Shield,
  UsersRound,
  Webhook,
} from 'lucide-react';

import { ApiError, auth } from '@/api/client';
import { qk, useLogout, useMe } from '@/api/queries';
import { Wordmark } from '@/components/brand/logo';
import { SUPPORTED, dirFor, type LangCode } from '@/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.fetchQuery({
        queryKey: qk.me,
        queryFn: auth.me,
        staleTime: 5_000,
      });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: AppShell,
});

import type { AdminRole, Entitlements } from '@/api/types';

type NavItem = {
  to: string;
  labelKey: string;
  icon: typeof Activity;
  groupKey?: string;
  /** Hidden unless this entitlement feature is true. Undefined = always shown. */
  requiresFeature?: keyof Entitlements['features'];
  /** Hidden unless the install is in demo mode (PAYHUB_DEMO_MODE=1). */
  demoOnly?: boolean;
  /**
   * Hidden unless the caller's effective role is in this set. Backend
   * is the source of truth for access; this is cosmetic gating so users
   * don't see nav items that 403 the moment they click.
   */
  allowedRoles?: AdminRole[];
};

// Role groupings reused across nav items. The backend matrix in
// app/core/rbac.py is authoritative — these mirror the relevant slices.
const FINANCIAL: AdminRole[] = ['admin', 'operator', 'accountant', 'viewer'];
const OPERATIONAL: AdminRole[] = ['admin', 'operator'];
const READ_GATEWAYS: AdminRole[] = ['admin', 'operator', 'viewer'];
const READ_AUDIT: AdminRole[] = ['admin', 'operator', 'accountant'];

const NAV: NavItem[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/payments', labelKey: 'nav.payments', icon: CreditCard },
  {
    to: '/webhooks/deliveries',
    labelKey: 'nav.webhooks',
    icon: Webhook,
    allowedRoles: FINANCIAL,
  },
  {
    to: '/settlements',
    labelKey: 'nav.settlements',
    icon: Scale,
    requiresFeature: 'reconciliation',
    allowedRoles: FINANCIAL,
  },
  { to: '/merchants', labelKey: 'nav.merchants', icon: KeyRound },
  {
    to: '/gateways',
    labelKey: 'nav.gateways',
    icon: Settings,
    allowedRoles: READ_GATEWAYS,
  },
  { to: '/audit', labelKey: 'nav.audit', icon: ScrollText, allowedRoles: READ_AUDIT },
  { to: '/users', labelKey: 'nav.users', icon: UsersRound, allowedRoles: ['admin'] },
  {
    to: '/security',
    labelKey: 'nav.security',
    icon: Shield,
    allowedRoles: OPERATIONAL,
  },
  {
    to: '/demo-requests',
    labelKey: 'nav.demo_requests',
    icon: Inbox,
    allowedRoles: ['admin', 'operator', 'support'],
    demoOnly: true,
  },
  { to: '/guide', labelKey: 'nav.user_guide', icon: BookOpen, groupKey: 'nav.help_group' },
  {
    to: '/integrations',
    labelKey: 'nav.psp_integrations',
    icon: Plug,
    groupKey: 'nav.help_group',
  },
  { to: '/api-docs', labelKey: 'nav.api_reference', icon: Code2, groupKey: 'nav.help_group' },
  {
    to: '/webhook-verification',
    labelKey: 'nav.webhook_signing',
    icon: Webhook,
    groupKey: 'nav.help_group',
  },
  {
    to: '/event-catalog',
    labelKey: 'nav.event_catalog',
    icon: Activity,
    groupKey: 'nav.help_group',
  },
];

function AppShell() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [navOpen, setNavOpen] = React.useState(false);

  React.useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = dirFor(i18n.language);
  }, [i18n.language]);

  const onLangChange = (next: string) => {
    i18n.changeLanguage(next as LangCode);
  };

  const onSignOut = () =>
    logout.mutate(undefined, { onSettled: () => navigate({ to: '/login' }) });

  const sidebar = (
    <SidebarBody
      navOpen={navOpen}
      onNavigate={() => setNavOpen(false)}
      me={me.data}
      i18nLang={i18n.language}
      onLangChange={onLangChange}
      onSignOut={onSignOut}
      signingOut={logout.isPending}
      t={t}
    />
  );

  return (
    <div className="min-h-dvh bg-background lg:grid lg:grid-cols-[260px_1fr]">
      {/* Mobile / tablet top bar — hidden on lg+ where the persistent sidebar shows. */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-2 border-b border-border bg-card/90 backdrop-blur px-3 py-2">
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="sm" aria-label="Open navigation">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="start" className="app-sidebar">
            <SheetTitle>{t('nav.dashboard')}</SheetTitle>
            <SheetDescription>{t('common.language')}</SheetDescription>
            {sidebar}
          </SheetContent>
        </Sheet>
        <Wordmark size="sm" />
      </header>

      {/* Persistent sidebar from lg up. */}
      <aside className="app-sidebar hidden lg:flex flex-col p-4">{sidebar}</aside>

      <main className="overflow-x-hidden">
        <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function SidebarBody({
  navOpen: _navOpen,
  onNavigate,
  me,
  i18nLang,
  onLangChange,
  onSignOut,
  signingOut,
  t,
}: {
  navOpen: boolean;
  onNavigate: () => void;
  me: ReturnType<typeof useMe>['data'];
  i18nLang: string;
  onLangChange: (next: string) => void;
  onSignOut: () => void;
  signingOut: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="px-2 py-3">
        <Wordmark size="md" />
      </div>
      <nav className="space-y-0.5 flex-1 mt-2 overflow-y-auto">
        {NAV.filter((item) => {
          if (item.requiresFeature && !me?.entitlements?.features[item.requiresFeature]) {
            return false;
          }
          if (item.demoOnly && !me?.demo_mode) {
            return false;
          }
          if (item.allowedRoles && me?.effective_role) {
            return item.allowedRoles.includes(me.effective_role);
          }
          return true;
        }).map((item, i, visible) => {
          const prev = visible[i - 1];
          const showGroupLabel = item.groupKey && item.groupKey !== prev?.groupKey;
          return (
            <React.Fragment key={item.to}>
              {showGroupLabel && item.groupKey && (
                <div className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {t(item.groupKey)}
                </div>
              )}
              <Link
                to={item.to}
                onClick={onNavigate}
                className={cn(
                  'group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors',
                  'hover:bg-accent hover:text-accent-foreground',
                  'data-[status=active]:bg-accent data-[status=active]:text-accent-foreground',
                )}
                activeProps={{ 'data-status': 'active' }}
              >
                <span
                  aria-hidden
                  className="absolute start-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary opacity-0 transition-opacity group-data-[status=active]:opacity-100"
                />
                <item.icon className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-accent-foreground group-data-[status=active]:text-primary" />
                {t(item.labelKey)}
              </Link>
            </React.Fragment>
          );
        })}
      </nav>

      <div className="border-t border-border pt-3 mt-3 space-y-2">
        {me?.entitlements?.expired && (
          <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-[11px] text-warning-foreground">
            {t('license.expired_banner', { defaultValue: 'License expired — contact your vendor to renew.' })}
          </div>
        )}
        {me && me.effective_role !== me.role && (
          <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-[11px] text-warning-foreground">
            {t('rbac.tier_degraded', {
              defaultValue:
                "Role {{role}} is not available on the {{tier}} tier — operating as {{effective}}.",
              role: me.role,
              tier: me.entitlements?.tier ?? '?',
              effective: me.effective_role,
            })}
          </div>
        )}
        {me && (
          <div className="flex items-center gap-3 rounded-md px-2 py-2">
            <div
              aria-hidden
              className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase"
            >
              {me.username.slice(0, 2)}
            </div>
            <div className="min-w-0 text-xs">
              <div className="truncate font-medium text-foreground">{me.username}</div>
              <div className="mt-0.5 flex flex-wrap gap-1">
                <Badge variant="secondary" className="text-[10px]">
                  {me.effective_role}
                </Badge>
                {me.entitlements && (
                  <Badge
                    variant={me.entitlements.tier === 'lite' ? 'outline' : 'secondary'}
                    className="text-[10px] uppercase tracking-wide"
                    title={
                      me.entitlements.is_dev
                        ? 'Dev license (no signed key)'
                        : `Customer: ${me.entitlements.customer}`
                    }
                  >
                    {me.entitlements.tier}
                    {me.entitlements.is_dev ? ' · dev' : ''}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 px-2">
          <Globe className="h-4 w-4 text-muted-foreground" aria-label={t('common.language')} />
          <Select value={i18nLang.split('-')[0]} onValueChange={onLangChange}>
            <SelectTrigger className="h-8 flex-1 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED.map((l) => (
                <SelectItem key={l.code} value={l.code}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={onSignOut}
          disabled={signingOut}
        >
          <LogOut className="h-4 w-4" />
          {t('common.sign_out')}
        </Button>
      </div>
    </div>
  );
}
